"use client";

import { useMemo, useState } from "react";

import {
  attachChartSubject,
  matchChartSubjects,
  type AccountChartSubject,
  type AccountReading,
  type ChartRelationship,
} from "./account-api";

const RELATIONSHIPS: { value: Exclude<ChartRelationship, "self">; label: string }[] = [
  { value: "spouse", label: "Spouse" },
  { value: "partner", label: "Partner" },
  { value: "parent", label: "Parent" },
  { value: "child", label: "Child" },
  { value: "sibling", label: "Sibling" },
  { value: "family", label: "Family member" },
  { value: "friend", label: "Friend" },
  { value: "other", label: "Other" },
];

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;

function privateReadingId(value: string): string | null {
  return value.trim().match(UUID_PATTERN)?.[0] ?? null;
}

export function chartRelationshipLabel(value: ChartRelationship): string {
  if (value === "self") return "My chart";
  return RELATIONSHIPS.find((item) => item.value === value)?.label ?? "Saved chart";
}

function useAsyncAction() {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function run(action: () => Promise<void>, success: string, fallback: string) {
    if (busy) return;
    setBusy(true);
    setMessage("");
    try {
      await action();
      setMessage(success);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : fallback);
    } finally {
      setBusy(false);
    }
  }

  return { busy, message, run, setBusy, setMessage };
}

export function StartChartCircle({
  reading,
  token,
  onRefresh,
}: {
  reading: AccountReading;
  token: string;
  onRefresh: () => Promise<void>;
}) {
  const action = useAsyncAction();
  return (
    <>
      <button
        type="button"
        disabled={action.busy}
        onClick={() =>
          void action.run(
            async () => {
              await attachChartSubject(token, reading.reading_id, "self");
              await onRefresh();
            },
            "Your chart circle is ready.",
            "Could not start your chart circle."
          )
        }
        className="mt-5 min-h-11 rounded-md border border-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-[var(--color-accent)] disabled:opacity-50"
      >
        {action.busy ? "Saving…" : "Start with my latest chart"}
      </button>
      <p aria-live="polite" className="mt-2 text-xs text-[var(--color-text-sub)]">
        {action.message}
      </p>
    </>
  );
}

export function AddChartSubjectForm({
  enabled,
  token,
  onRefresh,
}: {
  enabled: boolean;
  token: string;
  onRefresh: () => Promise<void>;
}) {
  const [privateLink, setPrivateLink] = useState("");
  const [relationship, setRelationship] = useState<Exclude<ChartRelationship, "self">>("family");
  const action = useAsyncAction();

  async function addChart() {
    const readingId = privateReadingId(privateLink);
    if (!readingId) {
      action.setMessage("Paste the private kundali link or its reading ID.");
      return;
    }
    await action.run(
      async () => {
        await attachChartSubject(token, readingId, relationship);
        await onRefresh();
        setPrivateLink("");
      },
      "Chart saved to your private circle.",
      "Could not save the private chart."
    );
  }

  return (
    <>
      <div className="mt-6 grid gap-3 border-t border-[var(--color-accent-border)] pt-5 sm:grid-cols-[1fr_10rem_auto] sm:items-end">
        <label className="grid gap-2 text-sm font-semibold">
          Private kundali link or ID
          <input
            value={privateLink}
            onChange={(event) => setPrivateLink(event.target.value)}
            placeholder="Paste /kundali/… link"
            className="min-h-11 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-raised)] px-3 text-sm outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-light)]"
          />
        </label>
        <label className="grid gap-2 text-sm font-semibold">
          Relationship
          <select
            value={relationship}
            onChange={(event) =>
              setRelationship(event.target.value as Exclude<ChartRelationship, "self">)
            }
            className="min-h-11 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-raised)] px-3 text-sm"
          >
            {RELATIONSHIPS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          disabled={action.busy || !privateLink.trim() || !enabled}
          onClick={() => void addChart()}
          className="min-h-11 rounded-md border border-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add chart
        </button>
      </div>
      <p aria-live="polite" className="mt-3 min-h-5 text-xs text-[var(--color-text-sub)]">
        {action.message}
      </p>
    </>
  );
}

export function CompareChartSubjects({
  subjects,
  token,
}: {
  subjects: AccountChartSubject[];
  token: string;
}) {
  const [firstSubject, setFirstSubject] = useState("");
  const [secondSubject, setSecondSubject] = useState("");
  const action = useAsyncAction();
  const selfSubject = subjects.find((subject) => subject.relationship_to_owner === "self");
  const first = firstSubject || selfSubject?.subject_id || subjects[0]?.subject_id || "";
  const secondOptions = useMemo(
    () => subjects.filter((subject) => subject.subject_id !== first),
    [first, subjects]
  );
  const second = secondOptions.some((subject) => subject.subject_id === secondSubject)
    ? secondSubject
    : secondOptions[0]?.subject_id || "";

  async function compare() {
    if (!first || !second || action.busy) return;
    action.setBusy(true);
    action.setMessage("");
    try {
      const result = await matchChartSubjects(token, first, second);
      if (!result.match_id) throw new Error("The saved comparison link was not returned.");
      window.location.assign(`/match/${encodeURIComponent(result.match_id)}`);
    } catch (error) {
      action.setMessage(error instanceof Error ? error.message : "Could not compare these charts.");
      action.setBusy(false);
    }
  }

  if (subjects.length < 2) return null;
  return (
    <>
      <div className="mt-6 grid gap-3 rounded-lg bg-[var(--color-surface-raised)] p-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <ChartSelect
          label="First chart"
          value={first}
          subjects={subjects}
          onChange={setFirstSubject}
        />
        <ChartSelect
          label="Second chart"
          value={second}
          subjects={secondOptions}
          onChange={setSecondSubject}
        />
        <button
          type="button"
          disabled={action.busy || !first || !second}
          onClick={() => void compare()}
          className="min-h-11 rounded-md bg-[var(--color-accent)] px-5 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {action.busy ? "Working…" : "Compare charts"}
        </button>
      </div>
      <p aria-live="polite" className="mt-3 min-h-5 text-xs text-[var(--color-text-sub)]">
        {action.message}
      </p>
    </>
  );
}

function ChartSelect({
  label,
  value,
  subjects,
  onChange,
}: {
  label: string;
  value: string;
  subjects: AccountChartSubject[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="grid gap-2 text-sm font-semibold">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-11 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-raised)] px-3 text-sm"
      >
        {subjects.map((subject) => (
          <option key={subject.subject_id} value={subject.subject_id}>
            {subject.display_name}
          </option>
        ))}
      </select>
    </label>
  );
}
